import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DEV_MOCK_KEY, getMockParams, setMockParams, MockParams } from "../../dev/mockData";
import { triggerProgressRefresh } from "../../contexts/ProgressContext";

// =====================================
// DEV TOOLKIT LOCK
// =====================================
const DEV_UNLOCK_KEY = "dev_toolkit_unlocked";
const DEV_PIN = process.env.REACT_APP_DEV_PIN || "1234";
const TAP_COUNT_REQUIRED = 5;
const TAP_WINDOW_MS = 3000;

const DevToolkitGate: React.FC = () => {
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(DEV_UNLOCK_KEY) === "true"
  );
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const tapTimestamps = useRef<number[]>([]);
  const pinInputRef = useRef<HTMLInputElement>(null);

  const handleTap = useCallback(() => {
    const now = Date.now();
    tapTimestamps.current = tapTimestamps.current.filter(
      (t) => now - t < TAP_WINDOW_MS
    );
    tapTimestamps.current.push(now);

    if (tapTimestamps.current.length >= TAP_COUNT_REQUIRED) {
      tapTimestamps.current = [];
      setShowPinPrompt(true);
      setPinError(false);
      setPin("");
    }
  }, []);

  useEffect(() => {
    if (showPinPrompt && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [showPinPrompt]);

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === DEV_PIN) {
      sessionStorage.setItem(DEV_UNLOCK_KEY, "true");
      setUnlocked(true);
      setShowPinPrompt(false);
    } else {
      setPinError(true);
      setPin("");
    }
  };

  if (unlocked) {
    return <DevNavBar />;
  }

  return (
    <>
      {/* Tap zone — bottom-right corner */}
      <div
        onClick={handleTap}
        className="fixed bottom-2 right-2 w-10 h-10 rounded-full bg-gray-800 bg-opacity-60 flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
        style={{ zIndex: 9998 }}
        title="Dev Toolkit"
      >
        <span className="text-yellow-400 text-lg select-none">&#9881;</span>
      </div>

      {/* PIN prompt overlay */}
      {showPinPrompt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => setShowPinPrompt(false)}
        >
          <form
            onSubmit={handlePinSubmit}
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-900 p-6 rounded-xl border border-yellow-400 w-72 space-y-4"
          >
            <div className="text-yellow-400 text-sm font-bold text-center tracking-wider">
              DEV TOOLKIT
            </div>
            <input
              ref={pinInputRef}
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => { setPin(e.target.value); setPinError(false); }}
              placeholder="Enter PIN"
              className={`w-full px-4 py-3 rounded-lg bg-gray-800 text-white text-center text-lg tracking-widest border-2 ${
                pinError ? "border-red-500" : "border-gray-600"
              } focus:border-yellow-400 outline-none`}
            />
            {pinError && (
              <div className="text-red-400 text-xs text-center">Wrong PIN</div>
            )}
            <button
              type="submit"
              className="w-full py-2 rounded-lg bg-yellow-400 text-gray-900 font-bold text-sm hover:bg-yellow-300 transition-colors"
            >
              UNLOCK
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export { DevToolkitGate };

interface RouteConfig {
  path: string;
  label: string;
  state?: Record<string, any>;
}

interface RouteGroup {
  label: string;
  color: string;
  routes: RouteConfig[];
}

const ROUTE_GROUPS: RouteGroup[] = [
  {
    label: "Auth",
    color: "#ef4444",
    routes: [
      { path: "/login", label: "Login" },
      { path: "/confirm_otp", label: "OTP" },
    ],
  },
  {
    label: "New Doctor Registration",
    color: "#3b82f6",
    routes: [
      { path: "/doctor_info_registration", label: "Doctor Info" },
      { path: "/doctor_documents", label: "Doctor Docs" },
      { path: "/home", label: "Home" },
    ],
  },
  {
    label: "Open New Clinic",
    color: "#10b981",
    routes: [
      { path: "/clinic_info_registration", label: "Clinic Info" },
      { path: "/clinic_photo", label: "Clinic Photos" },
      { path: "/review", label: "Review" },
      { path: "/agree_terms", label: "Terms" },
      { path: "/payment", label: "Payment" },
      { path: "/success", label: "Success" },
      { path: "/requests", label: "Requests" },
      { path: "/request/mock-req-001", label: "Request Detail", state: { requestType: "registration" } },
    ],
  },
  {
    label: "Onboard Existing Clinic",
    color: "#06b6d4",
    routes: [
      { path: "/clinic_info_registration", label: "Clinic Info" },
      { path: "/onboarding_photo", label: "Onboarding Photos" },
      { path: "/review", label: "Review" },
      { path: "/requests", label: "Requests" },
      { path: "/request/mock-req-001", label: "Request Detail", state: { requestType: "onboarding" } },
    ],
  },
  {
    label: "Close Clinic",
    color: "#f59e0b",
    routes: [
      { path: "/clinic-closure", label: "Own Clinic" },
      { path: "/existing-clinic-closure", label: "Existing Clinic" },
      { path: "/closure-documents", label: "Closure Docs" },
      { path: "/closure-review", label: "Closure Review" },
      { path: "/closure-agree-terms", label: "Closure Terms" },
      { path: "/payment", label: "Payment", state: { requestType: "closure", requestId: "mock-req-001" } },
      { path: "/requests", label: "Requests" },
      { path: "/closure-request/mock-req-001", label: "Closure Detail" },
    ],
  },
  {
    label: "Post-Approval",
    color: "#8b5cf6",
    routes: [
      { path: "/home", label: "Home" },
      { path: "/clinic", label: "Clinic Detail" },
      { path: "/clinic-management", label: "Clinic Mgmt" },
      { path: "/requests", label: "Requests" },
      { path: "/corrections", label: "Corrections" },
    ],
  },
];

// Store the original replaceState so we can restore it
const originalReplaceState = window.history.replaceState.bind(window.history);

function enableRedirectBlock() {
  window.history.replaceState = function (state: any, unused: string, url?: string | URL | null) {
    console.log(`[DevNav] Blocked replaceState redirect -> ${url}`);
    // No-op: swallow the redirect
  };
}

function disableRedirectBlock() {
  window.history.replaceState = originalReplaceState;
}

const DevNavBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [bypassActive, setBypassActive] = useState(
    () => sessionStorage.getItem("dev_nav_bypass") === "true"
  );
  const [mockActive, setMockActive] = useState(
    () => sessionStorage.getItem(DEV_MOCK_KEY) === "true"
  );
  const [mockConfigOpen, setMockConfigOpen] = useState(false);
  const [mockParams, setMockParamsState] = useState<MockParams>(getMockParams);
  const navigate = useNavigate();
  const location = useLocation();

  const updateParam = <K extends keyof MockParams>(key: K, value: MockParams[K]) => {
    const updated = { ...mockParams, [key]: value };
    setMockParamsState(updated);
    setMockParams({ [key]: value });
    triggerProgressRefresh();
  };

  // Apply/remove the history patch when bypass changes
  useEffect(() => {
    if (bypassActive) {
      enableRedirectBlock();
    } else {
      disableRedirectBlock();
    }
    return () => disableRedirectBlock();
  }, [bypassActive]);

  const toggleBypass = () => {
    const next = !bypassActive;
    setBypassActive(next);
    if (next) {
      sessionStorage.setItem("dev_nav_bypass", "true");
    } else {
      sessionStorage.removeItem("dev_nav_bypass");
    }
  };

  const toggleMock = () => {
    const next = !mockActive;
    setMockActive(next);
    if (next) {
      sessionStorage.setItem(DEV_MOCK_KEY, "true");
    } else {
      sessionStorage.removeItem(DEV_MOCK_KEY);
    }
    // Trigger a progress refresh so the context picks up the change
    triggerProgressRefresh();
  };

  const handleNavigate = (path: string, state?: Record<string, any>) => {
    // Enable bypass
    sessionStorage.setItem("dev_nav_bypass", "true");
    setBypassActive(true);

    // Temporarily restore replaceState for our own navigation, then re-block
    disableRedirectBlock();
    navigate(path, { replace: true, state });
    // Re-block after a tick (after React Router finishes its sync work)
    setTimeout(() => enableRedirectBlock(), 50);
  };

  return (
    <div style={{ zIndex: 9998 }} className="fixed bottom-0 left-0 right-0">
      {/* Expanded panel */}
      {isOpen && (
        <div className="bg-gray-900 text-white max-h-[50vh] overflow-y-auto border-t-2 border-yellow-400">
          {/* Dev toggles */}
          <div className="px-3 pt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={toggleBypass}
              className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                bypassActive
                  ? "bg-red-500 text-white"
                  : "bg-gray-700 text-gray-400"
              }`}
            >
              {bypassActive ? "REDIRECTS OFF" : "REDIRECTS ON"}
            </button>
            <button
              onClick={toggleMock}
              className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                mockActive
                  ? "bg-purple-500 text-white"
                  : "bg-gray-700 text-gray-400"
              }`}
            >
              {mockActive ? "MOCK DATA ON" : "MOCK DATA OFF"}
            </button>
          </div>

          {/* Mock Config Panel */}
          {mockActive && (
            <div className="px-3 pt-2">
              <button
                onClick={() => setMockConfigOpen(!mockConfigOpen)}
                className="text-xs font-bold text-purple-300 hover:text-purple-100 transition-colors"
              >
                {mockConfigOpen ? "▾" : "▸"} MOCK CONFIG
              </button>
              {mockConfigOpen && (
                <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {/* Has Clinic */}
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={mockParams.hasClinic}
                      onChange={(e) => updateParam("hasClinic", e.target.checked)}
                      className="accent-purple-500"
                    />
                    Has Clinic
                  </label>
                  {/* Clinic Status */}
                  <label className="flex items-center gap-2 text-gray-300">
                    Clinic Status
                    <select
                      value={mockParams.clinicStatus}
                      onChange={(e) => updateParam("clinicStatus", e.target.value as MockParams["clinicStatus"])}
                      className="bg-gray-700 text-white rounded px-1 py-0.5 text-xs flex-1"
                      disabled={!mockParams.hasClinic}
                    >
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="suspended">suspended</option>
                    </select>
                  </label>
                  {/* Profile Completed */}
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={mockParams.profileCompleted}
                      onChange={(e) => updateParam("profileCompleted", e.target.checked)}
                      className="accent-purple-500"
                    />
                    Profile Complete
                  </label>
                  {/* Documents Complete */}
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={mockParams.documentsComplete}
                      onChange={(e) => updateParam("documentsComplete", e.target.checked)}
                      className="accent-purple-500"
                    />
                    Docs Complete
                  </label>
                  {/* Request Status */}
                  <label className="flex items-center gap-2 text-gray-300">
                    Req Status
                    <select
                      value={mockParams.requestStatus}
                      onChange={(e) => updateParam("requestStatus", e.target.value as MockParams["requestStatus"])}
                      className="bg-gray-700 text-white rounded px-1 py-0.5 text-xs flex-1"
                    >
                      <option value="draft">draft</option>
                      <option value="submitted">submitted</option>
                      <option value="in_review">in_review</option>
                      <option value="approved">approved</option>
                      <option value="rejected">rejected</option>
                      <option value="changes_requested">changes_requested</option>
                    </select>
                  </label>
                  {/* Request Type */}
                  <label className="flex items-center gap-2 text-gray-300">
                    Req Type
                    <select
                      value={mockParams.requestType}
                      onChange={(e) => updateParam("requestType", e.target.value as MockParams["requestType"])}
                      className="bg-gray-700 text-white rounded px-1 py-0.5 text-xs flex-1"
                    >
                      <option value="registration">registration</option>
                      <option value="closure">closure</option>
                      <option value="onboarding">onboarding</option>
                    </select>
                  </label>
                  {/* Doctor Type */}
                  <label className="flex items-center gap-2 text-gray-300">
                    Doctor Type
                    <select
                      value={mockParams.doctorType}
                      onChange={(e) => updateParam("doctorType", e.target.value as MockParams["doctorType"])}
                      className="bg-gray-700 text-white rounded px-1 py-0.5 text-xs flex-1"
                    >
                      <option value="specialist">specialist</option>
                      <option value="practitioner">practitioner</option>
                    </select>
                  </label>
                  {/* Has Corrections (only when changes_requested) */}
                  {mockParams.requestStatus === "changes_requested" && (
                    <label className="flex items-center gap-2 text-gray-300">
                      <input
                        type="checkbox"
                        checked={mockParams.hasCorrections}
                        onChange={(e) => updateParam("hasCorrections", e.target.checked)}
                        className="accent-purple-500"
                      />
                      Has Corrections
                    </label>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="p-3 space-y-3">
            {ROUTE_GROUPS.map((group) => (
              <div key={group.label}>
                <div
                  className="text-xs font-bold uppercase tracking-wider mb-1.5 px-1"
                  style={{ color: group.color }}
                >
                  {group.label}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {group.routes.map((route, idx) => {
                    const isActive = location.pathname === route.path;
                    return (
                      <button
                        key={`${route.path}-${idx}`}
                        onClick={() => handleNavigate(route.path, route.state)}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                          isActive
                            ? "ring-2 ring-yellow-400 text-white"
                            : "text-gray-300 hover:text-white"
                        }`}
                        style={{
                          backgroundColor: isActive ? group.color : "rgba(255,255,255,0.1)",
                        }}
                        title={route.path}
                      >
                        {route.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Current path + state display */}
          <div className="px-3 pb-2 text-xs text-gray-500 font-mono">
            {location.pathname}
            {location.state && (
              <span className="text-gray-600 ml-2">
                {JSON.stringify(location.state)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-1.5 text-xs font-bold tracking-wider transition-colors ${
          isOpen
            ? "bg-yellow-400 text-gray-900"
            : "bg-gray-800 text-yellow-400 hover:bg-gray-700"
        }`}
      >
        {isOpen ? "CLOSE DEV NAV" : `DEV NAV  ${location.pathname}`}
        {!isOpen && bypassActive && " [NO REDIRECT]"}
        {!isOpen && mockActive && " [MOCK]"}
      </button>
    </div>
  );
};

export default DevNavBar;
